package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.ativos.Ativo
import br.com.htms.inspeccaocampo.data.ativos.AtivoStatus
import br.com.htms.inspeccaocampo.data.ativos.AtivosDados
import br.com.htms.inspeccaocampo.data.ativos.MovimentacaoTipo
import br.com.htms.inspeccaocampo.data.ativos.SolicStatus
import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab

/** Modelo "CONTROLE DE EQUIPAMENTOS ALUGADOS OBRAS" (PLANILHA MASTER CONTRATOS) + abas do módulo. */
object PlanilhaAtivos {
    private const val MASTER = "PLANILHA MASTER CONTRATOS"

    fun build(d: AtivosDados): ByteArray {
        val hoje = DatasColab.hoje()
        fun obra(id: String) = d.centrosCusto.firstOrNull { it.id == id }?.rotulo ?: "Sem obra"
        fun adm(ccId: String): String {
            val u = d.centrosCusto.firstOrNull { it.id == ccId }?.admUsername.orEmpty()
            if (u.isBlank()) return ""
            return d.usuarios.firstOrNull { it.username.equals(u, true) }?.nomeOuLogin ?: u
        }
        fun fornecedor(id: String) = d.fornecedores.firstOrNull { it.id == id }?.nome.orEmpty()
        fun status(a: Ativo) = AtivoStatus.rotulo(a.status)
        fun propriedade(a: Ativo) = if (a.alugado) "Alugado" else a.propriedade.lowercase().replaceFirstChar { it.uppercase() }.ifBlank { "Próprio" }

        val ativos = d.ativos.sortedWith(compareBy({ obra(it.centroCustoId) }, { it.categoria }, { it.descricao }))
        val cols = listOf(
            ColPlan("Nº", 7, TipoCol.NUMERO),
            ColPlan("OBRA", 30),
            ColPlan("ADM", 20),
            ColPlan("FORNECEDOR", 30),
            ColPlan("TIPO DE EQUIPAMENTO", 22),
            ColPlan("TIPO", 22),
            ColPlan("PLACA OU PATRIMONIO", 20),
            ColPlan("DESCRIÇÃO", 40),
            ColPlan("DATA DA CHEGADA", 14, TipoCol.DATA),
            ColPlan("VALOR MÊS", 15, TipoCol.MOEDA),
            ColPlan("VALOR DO EQUIPAMENTO", 17, TipoCol.MOEDA),
            ColPlan("VALOR EM ABERTO", 15, TipoCol.MOEDA),
            ColPlan("VALOR / ALUGUEL DIA", 15, TipoCol.MOEDA),
            ColPlan("UTILIZAÇÃO", 16),
            ColPlan("VALOR FINAL DE COMPRA", 17, TipoCol.MOEDA),
            ColPlan("STATUS", 15),
            ColPlan("PROPRIEDADE", 13),
            ColPlan("CÓDIGO", 12),
            ColPlan("RESPONSÁVEL", 22),
            ColPlan("INÍCIO CONTRATO", 14, TipoCol.DATA),
            ColPlan("TÉRMINO CONTRATO", 14, TipoCol.DATA),
            ColPlan("DIAS EM POSSE", 12, TipoCol.NUMERO),
            ColPlan("CUSTO ACUMULADO ALUGUEL", 18, TipoCol.MOEDA),
            ColPlan("OBSERVAÇÕES", 40)
        )
        fun l(t: String) = PlanKit.colRef(cols.indexOfFirst { it.titulo == t })
        val cChegada = l("DATA DA CHEGADA")
        val cMes = l("VALOR MÊS")
        val cStatus = l("STATUS")
        val cProp = l("PROPRIEDADE")
        val cDias = l("DIAS EM POSSE")

        val linhas = ativos.mapIndexed { i, a ->
            val chegada = PlanData.de(a.dataChegada)
            val emPosse = a.emPosse
            val dias = if (chegada != null && emPosse) (hoje + 25569 - chegada.serial).coerceAtLeast(0) else null
            val custo = if (a.alugado && dias != null && (a.valorMes ?: 0.0) > 0) dias * (a.valorMes ?: 0.0) / 30.0 else null
            listOf(
                i + 1,
                obra(a.centroCustoId),
                adm(a.centroCustoId),
                fornecedor(a.fornecedorId),
                a.categoria,
                a.modelo,
                a.placaPatrimonio,
                a.descricao,
                chegada,
                a.valorMes,
                a.valorEquipamento,
                a.valorEmAberto,
                Fx("IF($cMes{r}>0,$cMes{r}/30,\"\")", a.valorDia ?: ""),
                a.utilizacao,
                a.valorFinalCompra,
                status(a),
                propriedade(a),
                a.codigo,
                a.responsavel,
                PlanData.de(a.dataInicioContrato),
                PlanData.de(a.dataTerminoContrato),
                Fx(
                    "IF(OR($cChegada{r}=\"\",$cStatus{r}=\"Devolvido\",$cStatus{r}=\"Baixado\"),\"\",MAX(0,TODAY()-$cChegada{r}))",
                    dias ?: ""
                ),
                Fx(
                    "IF(AND($cProp{r}=\"Alugado\",ISNUMBER($cDias{r}),N($cMes{r})>0),$cDias{r}*$cMes{r}/30,\"\")",
                    custo ?: ""
                ),
                a.observacoes
            )
        }
        val master = PlanKit.abaDados(
            MASTER, "Controle de equipamentos e ativos — obras",
            "Gerado em ${PlanData.geradoEm()} · ${ativos.size} ativo(s). Filtre pelas setas do cabeçalho: o DASHBOARD acompanha o filtro.",
            cols, linhas
        )

        val movs = d.movimentacoes.filter { it.tipo != "EDICAO" }.sortedByDescending { it.data.ifBlank { DatasColab.isoDeDias((it.createdAt / 86_400_000L)) } }
        val movAba = PlanKit.abaDados(
            "MOVIMENTAÇÕES", "Movimentações de ativos",
            "Chegadas, transferências, manutenção, devoluções e baixas.",
            listOf(
                ColPlan("DATA", 12, TipoCol.DATA), ColPlan("MÊS", 10), ColPlan("ATIVO", 40), ColPlan("TIPO", 22),
                ColPlan("DE", 28), ColPlan("PARA", 28), ColPlan("STATUS RESULTANTE", 16), ColPlan("RESPONSÁVEL", 22),
                ColPlan("OBSERVAÇÃO", 40), ColPlan("REGISTRADO POR", 18)
            ),
            movs.map { m ->
                val a = d.ativos.firstOrNull { it.id == m.ativoId }
                val data = PlanData.de(m.data) ?: PlanData.deMs(m.createdAt)
                listOf(
                    data,
                    data?.let { DatasColab.isoDeDias(it.serial - 25569).take(7) }.orEmpty(),
                    a?.let { listOf(it.codigo, it.descricao).filter { s -> s.isNotBlank() }.joinToString(" - ") } ?: m.ativoId,
                    MovimentacaoTipo.rotulo(m.tipo),
                    if (m.deCentroCustoId.isBlank()) "" else obra(m.deCentroCustoId),
                    if (m.paraCentroCustoId.isBlank()) "" else obra(m.paraCentroCustoId),
                    if (m.statusResultante.isBlank()) "" else AtivoStatus.rotulo(m.statusResultante),
                    m.responsavel,
                    m.observacao,
                    m.createdBy
                )
            },
            cor = "26A69A"
        )

        val solics = d.solicitacoes.sortedByDescending { it.createdAt }
        val solAba = PlanKit.abaDados(
            "SOLICITAÇÕES", "Solicitações de movimentação e locação",
            "Situação atual de cada solicitação aberta no módulo.",
            listOf(
                ColPlan("CRIADA EM", 12, TipoCol.DATA), ColPlan("TIPO", 22), ColPlan("STATUS", 26), ColPlan("OBRA", 28),
                ColPlan("ATIVO", 34), ColPlan("PARA OBRA", 28), ColPlan("DATA", 12, TipoCol.DATA), ColPlan("INÍCIO", 12, TipoCol.DATA),
                ColPlan("FIM", 12, TipoCol.DATA), ColPlan("VEÍCULO", 20), ColPlan("CONDUTOR", 20), ColPlan("SOLICITANTE", 24),
                ColPlan("JUSTIFICATIVA / OBS.", 40)
            ),
            solics.map { s ->
                val a = d.ativos.firstOrNull { it.id == s.ativoId }
                listOf(
                    PlanData.deMs(s.createdAt),
                    MovimentacaoTipo.rotulo(if (s.locacao) "LOCACAO" else s.movTipo.ifBlank { s.tipo }),
                    SolicStatus.rotulo(s.status),
                    if (s.centroCustoId.isBlank()) "" else obra(s.centroCustoId),
                    a?.let { listOf(it.codigo, it.descricao).filter { x -> x.isNotBlank() }.joinToString(" - ") }.orEmpty(),
                    if (s.paraCentroCustoId.isBlank()) "" else obra(s.paraCentroCustoId),
                    PlanData.de(s.data),
                    PlanData.de(s.dataInicio),
                    PlanData.de(s.dataFim),
                    s.veiculo,
                    s.condutor,
                    s.solicitanteNome.ifBlank { s.solicitante },
                    listOf(s.justificativa, s.observacao).filter { it.isNotBlank() }.joinToString(" | ")
                )
            },
            cor = "FFB74D"
        )

        val cFornMaster = master.faixa("FORNECEDOR")
        val cMesMaster = master.faixa("VALOR MÊS")
        val fornAba = PlanKit.abaDados(
            "FORNECEDORES", "Fornecedores",
            "Ativos e valor mensal contratado por fornecedor (fórmulas sobre a planilha master).",
            listOf(
                ColPlan("FORNECEDOR", 34), ColPlan("CNPJ", 20), ColPlan("CONTATO", 22), ColPlan("TELEFONE", 16),
                ColPlan("E-MAIL", 28), ColPlan("ATIVO", 8), ColPlan("ATIVOS", 10, TipoCol.NUMERO), ColPlan("VALOR MÊS", 16, TipoCol.MOEDA)
            ),
            d.fornecedores.sortedBy { it.nome.lowercase() }.map { f ->
                val doF = d.ativos.filter { it.fornecedorId == f.id }
                listOf(
                    f.nome, f.cnpj, f.contato, f.telefone, f.email, f.ativo,
                    Fx("COUNTIF($cFornMaster,A{r})", doF.size),
                    Fx("SUMIF($cFornMaster,A{r},$cMesMaster)", doF.sumOf { it.valorMes ?: 0.0 })
                )
            },
            cor = "7E57C2"
        )

        val emPosse = ativos.filter { it.emPosse }
        val vis = master.visiveis()
        val st = master.faixa("STATUS")
        val emPosseFx = "($st<>\"Devolvido\")*($st<>\"Baixado\")"
        val kpis = listOf(
            KpiPlan("Ativos", master.contar(), ativos.size.toDouble()),
            KpiPlan("Em uso", master.contarSe("STATUS", "Em uso"), ativos.count { it.status == "EM_USO" }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_BAIXA),
            KpiPlan("Disponíveis", master.contarSe("STATUS", "Disponível"), ativos.count { it.status == "DISPONIVEL" }.toDouble()),
            KpiPlan("Em manutenção", master.contarSe("STATUS", "Em manutenção"), ativos.count { it.status == "MANUTENCAO" }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan(
                "Devolvidos / baixados",
                "${master.contarSe("STATUS", "Devolvido")}+${master.contarSe("STATUS", "Baixado")}",
                ativos.count { !it.emPosse }.toDouble()
            ),
            KpiPlan("Alugados em posse", "SUMPRODUCT((${master.faixa("PROPRIEDADE")}=\"Alugado\")*$emPosseFx*$vis)", emPosse.count { it.alugado }.toDouble()),
            KpiPlan("Custo mensal (em posse)", "SUMPRODUCT($emPosseFx*($cMesMaster)*$vis)", emPosse.sumOf { it.valorMes ?: 0.0 }, SharedOoxmlWriter.STYLE_KPI_MOEDA),
            KpiPlan(
                "Custo acumulado de aluguel", master.somar("CUSTO ACUMULADO ALUGUEL"),
                linhas.sumOf { ((it[22] as Fx).cache as? Double) ?: 0.0 }, SharedOoxmlWriter.STYLE_KPI_MOEDA
            ),
            KpiPlan("Valor dos equipamentos", master.somar("VALOR DO EQUIPAMENTO"), ativos.sumOf { it.valorEquipamento ?: 0.0 }, SharedOoxmlWriter.STYLE_KPI_MOEDA),
            KpiPlan("Valor em aberto", master.somar("VALOR EM ABERTO"), ativos.sumOf { it.valorEmAberto ?: 0.0 }, SharedOoxmlWriter.STYLE_KPI_MOEDA),
            KpiPlan("Movimentações", movAba.contar(), movs.size.toDouble()),
            KpiPlan(
                "Solicitações em aberto",
                "${solAba.contarSe("STATUS", SolicStatus.rotulo("PENDENTE"))}+${solAba.contarSe("STATUS", SolicStatus.rotulo("AGUARDANDO_FROTAS"))}+${solAba.contarSe("STATUS", SolicStatus.rotulo("DEVOLVIDA"))}",
                solics.count { it.aberta }.toDouble(), SharedOoxmlWriter.STYLE_CRIT_MEDIA
            )
        )
        val blocos = listOf(
            BlocoPlan("Ativos por status", "Status", PlanKit.contagem(ativos, ::status), { master.contarSe("STATUS", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Ativos por obra", "Obra", PlanKit.contagem(ativos) { obra(it.centroCustoId) }, { master.contarSe("OBRA", it) }, formulaTotal = master.contar()),
            BlocoPlan(
                "Valor mensal contratado por obra", "Obra",
                PlanKit.soma(ativos, { obra(it.centroCustoId) }) { it.valorMes ?: 0.0 },
                { master.somarSe("OBRA", it, "VALOR MÊS") }, moeda = true, formulaTotal = master.somar("VALOR MÊS")
            ),
            BlocoPlan(
                "Ativos por tipo de equipamento", "Tipo de equipamento",
                PlanKit.contagem(ativos) { it.categoria }, { master.contarSe("TIPO DE EQUIPAMENTO", it) }, formulaTotal = master.contar()
            ),
            BlocoPlan(
                "Ativos por fornecedor", "Fornecedor",
                PlanKit.contagem(ativos) { fornecedor(it.fornecedorId) }, { master.contarSe("FORNECEDOR", it) }, formulaTotal = master.contar()
            ),
            BlocoPlan("Alugados x próprios", "Propriedade", PlanKit.contagem(ativos, ::propriedade), { master.contarSe("PROPRIEDADE", it) }, SharedExcelChartType.PIE),
            BlocoPlan(
                "Movimentações por tipo", "Tipo",
                PlanKit.contagem(movs) { MovimentacaoTipo.rotulo(it.tipo) }, { movAba.contarSe("TIPO", it) }, SharedExcelChartType.PIE
            ),
            BlocoPlan(
                "Movimentações por mês", "Mês",
                PlanKit.contagem(movs) { m -> (PlanData.de(m.data) ?: PlanData.deMs(m.createdAt))?.let { DatasColab.isoDeDias(it.serial - 25569).take(7) }.orEmpty() }
                    .filter { it.first.isNotBlank() }.sortedBy { it.first }.takeLast(12),
                { movAba.contarSe("MÊS", it) }
            ),
            BlocoPlan("Solicitações por status", "Status", PlanKit.contagem(solics) { SolicStatus.rotulo(it.status) }, { solAba.contarSe("STATUS", it) }, SharedExcelChartType.PIE)
        )
        val dash = PlanKit.dashboard(
            "DASHBOARD", "Dashboard — Controle de Ativos",
            "Os números acompanham o filtro da ${MASTER} (e das abas MOVIMENTAÇÕES e SOLICITAÇÕES). Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos
        )
        return PlanKit.gerar(listOf(dash, master.sheet, movAba.sheet, solAba.sheet, fornAba.sheet))
    }
}
